import { create } from 'zustand';
import type { LookConfig } from '@/looks';
import type { GeneratedPalette } from '@/theme/apply-theme';

/**
 * The runtime-overridable subset of `site_settings.theme_config` — exactly what
 * the live storefront applies at runtime (PublicLayout.tsx) and what the Design
 * Sandbox lets an admin try. Mirrors the persisted shape so publishing is a
 * straight copy. (Site structure is out of scope by design.)
 */
export interface DesignThemeConfig {
  primary_color?: string;
  accent_color?: string;
  bg_warm?: string;
  font_heading?: string;
  font_body?: string;
  look?: LookConfig;
  /**
   * 'full' opts the published palette into the whole-neutral swap (brown/text/bg
   * too); absent/'base' keeps the primary+accent+warm-only expansion. Stamped on
   * publish from the sandbox; see expandThemeConfig (backend).
   */
  palette_scope?: 'base' | 'full';
  /**
   * Scheme a visitor with no stored choice gets ('system' follows the OS).
   * Set by dark-first templates; absent = light. Applied by RuntimeBrand.
   */
  color_scheme_default?: 'light' | 'dark' | 'system';
  /**
   * Explicit seeds for the dark scheme (a dark-first template designed it);
   * absent = derived from the light colours. Expanded server-side into
   * `generated.dark` like everything else.
   */
  dark_palette?: { primary: string; accent: string; surface: string };
  /**
   * The derived full palette (sage/brown/accent/warm + the ink/surface/line
   * neutral system + dark). Attached only to the config pushed into the preview
   * frame so the whole storefront repaints at full fidelity — it is NEVER saved
   * to a draft or sent on publish (the server re-derives it on read via
   * expandThemeConfig).
   */
  generated?: GeneratedPalette;
}

interface DesignPreviewState {
  /**
   * When set (inside a `?design_preview=1` storefront iframe), `useSettings()`
   * substitutes this for the live `theme_config` so the whole storefront
   * renders the candidate design — for this session only. Null = no preview.
   */
  previewThemeConfig: DesignThemeConfig | null;
  setPreviewThemeConfig: (config: DesignThemeConfig | null) => void;
  /**
   * True only inside an active design-preview iframe. Storefront chrome that
   * shouldn't appear in a clean preview (e.g. the floating admin bar) reads this
   * to hide itself.
   */
  isPreviewFrame: boolean;
  setIsPreviewFrame: (value: boolean) => void;
}

/**
 * Holds the candidate design pushed into a preview iframe by the sandbox host.
 * Lives only in the iframe's own tab; never persisted, never seen by real
 * visitors. Read by `useSettings()` (web/src/hooks/useProducts.ts).
 */
export const useDesignPreviewStore = create<DesignPreviewState>((set) => ({
  previewThemeConfig: null,
  setPreviewThemeConfig: (config) => set({ previewThemeConfig: config }),
  isPreviewFrame: false,
  setIsPreviewFrame: (value) => set({ isPreviewFrame: value }),
}));
